/**
 * Intercom Configuration
 *
 * This file contains all Intercom-related configuration including app ID,
 * survey IDs, and environment-specific settings.
 */

export const INTERCOM_CONFIG = {
  APP_ID: import.meta.env.VITE_INTERCOM_APP_ID || "qwm4lzvc",
  LITE_APP_ID: import.meta.env.VITE_INTERCOM_LITE_APP_ID || "kqd8mytz",
  // Test workspace for debug context grabber feature
  TEST_APP_ID: import.meta.env.VITE_INTERCOM_TEST_APP_ID || "bl0e7mz6",
  SURVEY_IDS: {
    FEEDBACK: import.meta.env.VITE_INTERCOM_FEEDBACK_SURVEY_ID || "51581758",
    SMART_SYNC:
      import.meta.env.VITE_INTERCOM_SMART_SYNC_SURVEY_ID || "51586918",
  },
} as const;

/**
 * Determines which Intercom workspace to use based on environment and feature flag.
 *
 * Priority:
 * 1. VITE_USE_INTERCOM_TEST_WORKSPACE env var (explicit override)
 * 2. Development environment (auto-use test workspace)
 * 3. Production/staging (use production workspace)
 *
 * @returns The Intercom app ID to use
 */
export const getActiveIntercomAppId = (): string => {
  // Explicit feature flag override (highest priority)
  const useTestWorkspace =
    import.meta.env.VITE_USE_INTERCOM_TEST_WORKSPACE === "true";
  if (useTestWorkspace) {
    console.log("[Intercom] Using TEST workspace (feature flag enabled)");
    return INTERCOM_CONFIG.TEST_APP_ID;
  }

  // Auto-use test workspace in development and staging
  const environment = import.meta.env.VITE_APP_ENV;
  if (environment === "development" || environment === "staging") {
    console.log(`[Intercom] Using TEST workspace (${environment} environment)`);
    return INTERCOM_CONFIG.TEST_APP_ID;
  }

  // Production/staging uses production workspace
  console.log(
    `[Intercom] Using PRODUCTION workspace (${environment} environment)`
  );
  return INTERCOM_CONFIG.LITE_APP_ID;
};

/**
 * Survey ID constants for better maintainability
 */
export const SURVEY_IDS = {
  FEEDBACK: INTERCOM_CONFIG.SURVEY_IDS.FEEDBACK,
  SMART_SYNC: INTERCOM_CONFIG.SURVEY_IDS.SMART_SYNC,
} as const;
